// Recruiter filter options for Interested Feed quick-filters.
// Selected chips are combined into a search query that leverages the
// existing multi-index backend pipeline (Location, Role-Level, RLL indices).

export const RECRUITER_LOCATION_SUGGESTIONS = [
  // Top cities (data-confirmed from candidate base)
  'Berlin', 'München', 'Düsseldorf', 'Hamburg', 'Köln', 'Frankfurt',
  'Aachen', 'Koblenz', 'Stuttgart', 'Bonn', 'Karlsruhe', 'Dresden',
  'Münster', 'Hannover', 'Dortmund',
  // DACH
  'Wien', 'Zürich', 'Basel',
  // International
  'London', 'Cambridge', 'San Francisco', 'New York', 'Amsterdam',
  // Regions & semantic
  'NRW', 'RLP', 'DACH', 'Schweiz', 'Remote', 'Hybrid',
  'Open to Relocate', 'Europe', 'USA', 'UK', 'Worldwide',
];

export const RECRUITER_ROLE_SUGGESTIONS = [
  // Tech - high-demand new roles first
  'Founding Engineer', 'Fullstack AI Engineer', 'AI Engineer', 'AI Researcher',
  'Forward Deployed Engineer', 'GTM Engineer',
  'Fullstack Engineer', 'Frontend Developer', 'Backend Developer',
  'Software Engineer', 'ML Engineer', 'DevOps Engineer',
  'Data Engineer', 'Data Scientist', 'Data Analyst', 'Analytics Engineer',
  'iOS Developer', 'Android Developer', 'QA Engineer',
  // Startup / VC / Biz
  'Product Manager', 'Technical Product Manager', 'Chief of Staff',
  'Founders Associate', 'VC Analyst', 'Business Analyst', 'Operations Manager',
  'Co-Founder', 'CTO',
  // Consulting & Finance
  'Strategy Consultant', 'Management Consultant',
  'Financial Analyst', 'Investment Banking Analyst',
  // Diversity
  'UX Designer', 'Marketing Manager', 'Sales Manager',
  'HR Manager', 'Customer Success Manager', 'Content Creator',
];

export const RECRUITER_SKILLS_SUGGESTIONS = [
  // Core languages & frameworks
  'React', 'TypeScript', 'Python', 'Node.js', 'Java', 'Go', 'Rust', 'SQL',
  'FastAPI', 'Next.js', 'Django', 'Spring Boot', 'Flutter',
  // AI / ML stack
  'AI / Machine Learning', 'LLMs', 'Prompt Engineering', 'RAG',
  'OpenAI', 'Mistral', 'LangChain', 'AI Agents',
  'PyTorch', 'Hugging Face', 'Computer Vision', 'NLP',
  'PDF AI Processing', 'AI Frameworks',
  // Infra & data
  'Cloud / AWS', 'Kubernetes', 'Docker', 'Terraform',
  'Supabase', 'Pinecone', 'Qdrant', 'PostgreSQL', 'MongoDB', 'Redis',
  // Domains
  'Fintech', 'SaaS', 'B2B', 'E-Commerce', 'Climate Tech',
  'Health Tech', 'Deep Tech', 'Cyber Security',
  // Background
  'Startup Experience', 'Consulting Background', 'Finance Background',
  'STEM', 'Entrepreneurial', 'Open Source', 'Research',
];

// Fixed level list - matches the predefined levels from CandidateSearch.
// Multi-select, no refresh, no custom input needed.
export const RECRUITER_LEVEL_OPTIONS = [
  { value: 'High School Student', labelEn: 'High School', labelDe: 'Schüler:in' },
  { value: 'Apprentice / Vocational Training', labelEn: 'Apprentice', labelDe: 'Azubi / Lehrling' },
  { value: 'Working Student / Intern', labelEn: 'Working Student / Intern', labelDe: 'Werkstudent:in / Praktikant:in' },
  { value: 'Bachelor / Undergraduate Student', labelEn: 'Bachelor / Undergraduate', labelDe: 'Bachelor-Student:in' },
  { value: 'Master / Graduate Student', labelEn: 'Master / Graduate', labelDe: 'Master-Student:in' },
  { value: 'PhD Student', labelEn: 'PhD Student', labelDe: 'Doktorand:in' },
  { value: 'Junior / Entry-Level / New Grad (0-2 years)', labelEn: 'Junior (0-2y)', labelDe: 'Junior (0-2 J.)' },
  { value: 'Qualified Worker (after apprenticeship)', labelEn: 'Qualified Worker', labelDe: 'Fachkraft' },
  { value: 'Mid-Level (2-5 years)', labelEn: 'Mid-Level (2-5y)', labelDe: 'Mid-Level (2-5 J.)' },
  { value: 'Senior (5+ years)', labelEn: 'Senior (5+y)', labelDe: 'Senior (5+ J.)' },
  { value: 'Lead / Management', labelEn: 'Lead / Management', labelDe: 'Lead / Management' },
  { value: 'Expert / Principal (15+ years)', labelEn: 'Expert / Principal', labelDe: 'Expert:in / Spezialist:in' },
  { value: 'Entrepreneur / Self-Employed', labelEn: 'Entrepreneur', labelDe: 'Unternehmer:in' },
];

// Map keyword patterns (case-insensitive) → RECRUITER_LEVEL_OPTIONS value
// Handles: singular, plural, Gendersternchen, parenthesized forms
const LEVEL_KEYWORDS: [RegExp, string][] = [
  // Order matters: more specific patterns first to avoid false matches
  [/\(?\b(working students?|werkstudent:?inn?e?n?|werkstudent)\b\)?/i, 'Working Student / Intern'],
  [/\(?\b(interns?(?:ship)?|praktikant:?inn?e?n?|praktikant|pflichtpraktik(?:um)?)\b\)?/i, 'Working Student / Intern'],
  [/\(?\b(entry[- ]level|new grads?|berufseinsteiger:?inn?e?n?|berufseinsteiger|absolvent:?inn?e?n?|absolvent)\b\)?/i, 'Junior / Entry-Level / New Grad (0-2 years)'],
  [/\(?\b(junior|jr\.)\b\)?/i, 'Junior / Entry-Level / New Grad (0-2 years)'],
  [/\(?\b(mid[- ]?level|mid[- ]?senior|erfahren)\b\)?/i, 'Mid-Level (2-5 years)'],
  [/\(?\b(senior|sr\.)\b\)?/i, 'Senior (5+ years)'],
  [/\(?\b(lead|head of|leitung|teamlead|team lead|teamleiter:?inn?e?n?|teamleiter|führungskräfte?|vp|vice president|director)\b\)?/i, 'Lead / Management'],
  [/\(?\b(principal|staff|expert:?inn?e?n?|expert|distinguished|spezialist:?inn?e?n?|spezialist)\b\)?/i, 'Expert / Principal (15+ years)'],
  [/\(?\b(phd|doktorand:?inn?e?n?|doktorand|doctoral|promotion)\b\)?/i, 'PhD Student'],
  [/\(?\b(master student|graduate student|master-student:?inn?e?n?)\b\)?/i, 'Master / Graduate Student'],
  [/\(?\b(bachelor student|undergraduate|bachelor-student:?inn?e?n?)\b\)?/i, 'Bachelor / Undergraduate Student'],
  [/\(?\b(apprentices?|azubis?|auszubildende:?r?|auszubildende|ausbildung|lehrlinge?|vocational)\b\)?/i, 'Apprentice / Vocational Training'],
  [/\(?\b(founders?|gründer:?inn?e?n?|gründer|entrepreneurs?|co-?founders?|ceo|cto|coo|cfo|unternehmer:?inn?e?n?|unternehmer|selbstständig)\b\)?/i, 'Entrepreneur / Self-Employed'],
  [/\(?\b(schüler:?inn?e?n?|schüler|abiturient:?inn?e?n?|abiturient|high school)\b\)?/i, 'High School Student'],
  [/\(?\b(fachkräfte?|fachkraft|meister:?inn?e?n?|qualified workers?)\b\)?/i, 'Qualified Worker (after apprenticeship)'],
];

/**
 * Parse a job title like "Senior AI Engineer" or
 * "Fullstack AI Engineer (Werkstudent:innen)" into { role, level }.
 * Strips the level keyword (including surrounding parens) from the title.
 */
export function parseRoleLevel(title: string): { role: string; level: string | null } {
  for (const [pattern, levelValue] of LEVEL_KEYWORDS) {
    if (pattern.test(title)) {
      const cleanRole = title
        .replace(pattern, '')
        .replace(/\(\s*\)/g, '')       // remove empty parens left behind
        .replace(/\s*&\s*$|^\s*&\s*/g, '') // remove trailing/leading "&"
        .replace(/\s{2,}/g, ' ')
        .replace(/^[\s,·\-]+|[\s,·\-]+$/g, '')
        .trim();
      return { role: cleanRole || title, level: levelValue };
    }
  }
  return { role: title, level: null };
}
